import React from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  Flex,
  Tooltip as InfoTooltip,
  Switch,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaCircleInfoRegular } from '@enechain/ecloud-icons'

import {
  MarginReportSeriesDef,
  MarginReportSeriesFill,
  MarginReportSeriesKey,
  MarginReportSeriesStyle,
  assertUnreachableDotShape,
  isSeriesAvailable,
  marginReportSeries,
  resolveMarginReportSeriesStyle,
} from './series'

const SWATCH_WIDTH = 24
const SWATCH_HEIGHT = 12
const SWATCH_CENTER_Y = SWATCH_HEIGHT / 2
const SWATCH_CENTER_X = SWATCH_WIDTH / 2
// 凡例スウォッチは 24x12px の小さな枠に収めるため半径 4px に固定している。
// チャート本体側のドットサイズ（標準 5px / 縮小レイアウト時 4px）とは別スケールなので
// 揃える必要はない。
const DOT_SIZE = 4
// 'band' スウォッチ（破線枠＋薄塗りの角丸帯）の寸法。
// プロトタイプ実測（32x16 の svg 内、rect x=0 y=3 width=32 height=10 rx=2, stroke-width=1）を
// スウォッチ全体（24x12）に比例縮小したもの。stroke-width はプロトタイプ実測値をそのまま定数化。
const BAND_Y = 2
const BAND_HEIGHT = 8
const BAND_RX = 2
const BAND_STROKE_WIDTH = 1

// series.ts のスタイル定義から凡例スウォッチ（線＋ドット）を描画する汎用コンポーネント。
// 色・線種・ドット形状は series.ts が唯一の情報源で、ここではハードコードしない。
const MarginReportSeriesSwatch: React.FC<{
  style: MarginReportSeriesStyle
}> = ({ style }) => {
  const { stroke, strokeDasharray, strokeWidth, dot, fill, legendSwatch } =
    style
  const cx = SWATCH_CENTER_X
  const cy = SWATCH_CENTER_Y

  const renderBand = (bandFill: MarginReportSeriesFill): React.ReactNode => {
    const fillColor = bandFill.kind === 'solid' ? bandFill.color : 'none'
    return (
      <rect
        x={0}
        y={BAND_Y}
        width={SWATCH_WIDTH}
        height={BAND_HEIGHT}
        rx={BAND_RX}
        fill={fillColor}
        stroke={stroke}
        strokeWidth={BAND_STROKE_WIDTH}
        strokeDasharray={strokeDasharray}
      />
    )
  }

  const renderDot = (): React.ReactNode => {
    switch (dot) {
      case 'circle':
        return <circle cx={cx} cy={cy} r={DOT_SIZE} fill={stroke} />
      case 'square':
        return (
          <rect
            x={cx - DOT_SIZE}
            y={cy - DOT_SIZE}
            width={DOT_SIZE * 2}
            height={DOT_SIZE * 2}
            fill={stroke}
          />
        )
      case 'diamond':
        return (
          <polygon
            points={`${cx},${cy - DOT_SIZE} ${cx + DOT_SIZE},${cy} ${cx},${cy + DOT_SIZE} ${cx - DOT_SIZE},${cy}`}
            fill={stroke}
          />
        )
      case 'triangle':
        return (
          <polygon
            points={`${cx},${cy - DOT_SIZE} ${cx - DOT_SIZE},${cy + DOT_SIZE} ${cx + DOT_SIZE},${cy + DOT_SIZE}`}
            fill={stroke}
          />
        )
      case 'hexagon': {
        const points = Array.from({ length: 6 }, (_, i) => {
          const angle = -Math.PI / 2 + (Math.PI / 3) * i
          return `${cx + DOT_SIZE * Math.cos(angle)},${cy + DOT_SIZE * Math.sin(angle)}`
        }).join(' ')
        return <polygon points={points} fill={stroke} />
      }
      case 'none':
        return null
      default:
        return assertUnreachableDotShape(dot)
    }
  }

  return (
    <svg
      width={SWATCH_WIDTH}
      height={SWATCH_HEIGHT}
      viewBox={`0 0 ${SWATCH_WIDTH} ${SWATCH_HEIGHT}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {legendSwatch === 'band' ? (
        renderBand(fill)
      ) : (
        <>
          <path
            d={`M0 ${cy}H${SWATCH_WIDTH}`}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
          />
          {renderDot()}
        </>
      )}
    </svg>
  )
}

// 凡例1系列分の「ラベル + 説明ツールチップ + スウォッチ」。
// Checkbox 側・Switch 側の両方から共通で使う。
const MarginReportSeriesLabel: React.FC<{
  def: MarginReportSeriesDef
  isHybrid: boolean
}> = ({ def, isHybrid }) => {
  const { t } = useTranslation(['component'])
  return (
    <Flex alignItems="center">
      <Text>{t(def.labelKey)}</Text>
      {def.descriptionKey !== undefined && (
        <InfoTooltip
          showArrow
          label={t(def.descriptionKey)}
          contentProps={{ whiteSpace: 'pre-wrap' }}
          placement="top"
        >
          <Box marginLeft="1px" display="flex" alignItems="center">
            <FaCircleInfoRegular />
          </Box>
        </InfoTooltip>
      )}
      <Box marginLeft="1" display="flex" alignItems="center">
        <MarginReportSeriesSwatch
          style={resolveMarginReportSeriesStyle(def, isHybrid)}
        />
      </Box>
    </Flex>
  )
}

export type MarginReportLegendProps = {
  // hybridOnly 系列の表示制御と deposits のスタイル切替に使う
  isHybrid: boolean
  visibility: Record<MarginReportSeriesKey, boolean>
  onToggleVisibility: (key: MarginReportSeriesKey, visible: boolean) => void
}

export const MarginReportLegend: React.FC<MarginReportLegendProps> = ({
  isHybrid,
  visibility,
  onToggleVisibility,
}) => {
  const visibleSeries = marginReportSeries.filter((def) =>
    isSeriesAvailable(def, isHybrid),
  )
  const checkboxSeries = visibleSeries.filter(
    (def) => def.control === 'checkbox',
  )
  const switchSeries = visibleSeries.filter((def) => def.control === 'switch')

  return (
    <Flex
      flexWrap="wrap"
      gap="5"
      alignItems="center"
      justifyContent="space-between"
      marginBottom="2"
    >
      <Flex flexWrap="wrap" gap="5" alignItems="center">
        {checkboxSeries.map((def) => (
          <Checkbox
            key={def.key}
            checked={visibility[def.key]}
            onChange={() => onToggleVisibility(def.key, !visibility[def.key])}
          >
            <MarginReportSeriesLabel def={def} isHybrid={isHybrid} />
          </Checkbox>
        ))}
      </Flex>
      {switchSeries.map((def) => (
        <Flex key={def.key} alignItems="center" gap="3">
          <Switch
            checked={visibility[def.key]}
            onChange={() => onToggleVisibility(def.key, !visibility[def.key])}
          />
          <MarginReportSeriesLabel def={def} isHybrid={isHybrid} />
        </Flex>
      ))}
    </Flex>
  )
}
